import { Component, OnInit, OnDestroy, ViewChild, signal, computed, WritableSignal, Signal, effect, ChangeDetectorRef } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Subject, Observable, Subscription, timer, combineLatest } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged, finalize, startWith, map, switchMap, tap } from 'rxjs/operators';
import { ExternalInvoice } from '@app/types/external-invoice.type';
import { ExternalInvoiceService } from '@app/services/entities/external-invoice.service';
import { CustomSnackBarService } from '@app/shared/components/custom-snack-bar/custom-snack-bar.service';
import { CustomMessageService } from '@app/shared/components/custom-message/custom-message.service';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '@app/material.module';
import { CustomTitleComponent } from '@app/shared/components/custom-title/custom-title.component';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDialog } from '@angular/material/dialog';
import { ObservationsDialogComponent } from '@app/invoice/invoice-monitor/observations-dialog/observations-dialog.component';
import { UploadStatusDialogComponent } from '../invoice-monitor/upload-status-dialog/upload-status-dialog.component';
import { ValidationService } from '@app/services/utils/validation.service';
import { StatusTranslatorService } from '@app/services/utils/status-translator.service';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { AnalysisService } from '@app/services/entities/analysis.service';
import { SimpleExcelService } from '@app/services/utils/simple-excel.service';
import { AnalysisQueueState } from '@app/types';
import { AnalysisStatusCardComponent } from '@app/external-document/analysis-status-card/analysis-status-card.component';
import { MatMenuModule } from '@angular/material/menu';
import { UploadArmstrongDialogComponent } from '../upload-armstrong-dialog/upload-armstrong-dialog.component';
import { EditExternalInvoiceDialogComponent } from './edit-external-invoice-dialog/edit-external-invoice-dialog.component';

@Component({
  selector: 'app-external-invoice-monitor',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, MaterialModule, ClipboardModule, AnalysisStatusCardComponent, MatTabsModule, MatMenuModule],
  templateUrl: './external-invoice-monitor.component.html',
  styleUrls: ['./external-invoice-monitor.component.scss']

})
export class ExternalInvoiceMonitorComponent implements OnInit, OnDestroy {
  filtroForm: FormGroup;

  // --- NUEVO SIGNAL PARA FILTROS ---
  filtrosSignal: WritableSignal<any> = signal({});

  invoicesSignal: Signal<ExternalInvoice[]>;
  filteredInvoicesSignal: Signal<ExternalInvoice[]>;
  paginatedInvoicesSignal: Signal<ExternalInvoice[]>;

  // Loading State
  apiLoadingSignal: Signal<boolean>;
  isFilteringSignal: WritableSignal<boolean> = signal(false);
  isLoadingSignal: Signal<boolean>;

  isUpdatingStatusSignal: WritableSignal<boolean> = signal(false);

  totalFacturasFiltradasSignal: Signal<number>;

  // --- Lógica de Selección ---
  todasSeleccionadasSignal: Signal<boolean>;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  pageEventSignal: WritableSignal<PageEvent> = signal<PageEvent>({ pageIndex: 0, pageSize: 20, length: 0 });

  estatusDisponibles = computed(() => {
    const statuses = this.statusTranslator.getStatuses()();
    return statuses
      .filter(s => !s.deleted)
      .sort((a, b) => a.order - b.order)
      .map(s => s.status);
  });
  estatusSeleccionadoParaCambio: string = '';

  // --- Autocompletado ---
  filteredEmisores$: Observable<string[]>;
  filteredClientes$: Observable<string[]>;
  filteredRfcs$: Observable<string[]>;

  // --- Lógica de Polling en tiempo real ---
  analysisStatusSignal: Signal<AnalysisQueueState>;
  private invoicePollingSubscription?: Subscription;
  private currentRequestSubscription?: Subscription; // Para cancelar peticiones al cambiar de tab
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private externalInvoiceService: ExternalInvoiceService,
    private snackBar: CustomSnackBarService,
    private customMessage: CustomMessageService,
    private dialog: MatDialog,
    private router: Router,
    private validationService: ValidationService,
    private statusTranslator: StatusTranslatorService,
    private analysisService: AnalysisService,
    private simpleExcel: SimpleExcelService,
    private cdr: ChangeDetectorRef
  ) {
    this.filtroForm = this.fb.group({
      textoGeneral: [''],
      emisor: [''],
      cliente: [''],
      rfc: [''],
      estatus: [''],
    });

    this.apiLoadingSignal = this.externalInvoiceService.isLoadingSignal;
    this.invoicesSignal = this.externalInvoiceService.externalInvoicesSignal;
    this.analysisStatusSignal = this.analysisService.state;

    // Merge API loading and Local filtering
    this.isLoadingSignal = computed(() => this.apiLoadingSignal() || this.isFilteringSignal());

    this.filteredInvoicesSignal = computed(() => {
      const invoices = this.invoicesSignal();
      // --- MODIFICACIÓN: Usar el signal de filtros ---
      const filters = this.filtrosSignal();
      const searchTerm = (filters.textoGeneral || '').toLowerCase().trim();
      const activeTab = this.activeTabSignal();

      return invoices.filter(inv => {
        if (filters.estatus && inv.estatus !== filters.estatus) return false;
        if (filters.emisor && inv.emisorNombre !== filters.emisor) return false;
        if (filters.cliente && inv.receptorNombre !== filters.cliente) return false;
        if (filters.rfc && inv.receptorRFC !== filters.rfc) return false;

        if (searchTerm) {
          let searchableText: string;

          // Para Remisiones (Tab 2), buscar en campos específicos de remisión
          if (activeTab === 2) {
            searchableText = [
              inv.folioRemision,
              inv.folioContrato,
              inv.folioLicitacion,
              inv.ordenSuministro,
              inv.proveedorNombre,
              inv.proveedorRFC,
              inv.emisorNombre,
              inv.receptorNombre,
              inv.receptorRFC,
              inv.total?.toString(),
              inv.estatus,
              inv.serie
            ].join(' ').toLowerCase();
          } else {
            // Para Facturas/Pre-facturas, usar campos tradicionales
            searchableText = [
              inv.folio,
              inv.emisorNombre,
              inv.receptorNombre,
              inv.receptorRFC,
              inv.total?.toString(),
              inv.estatus,
              inv.serie,
              inv.uuid
            ].join(' ').toLowerCase();
          }

          if (!searchableText.includes(searchTerm)) return false;
        }
        return true;
      });
    });

    this.totalFacturasFiltradasSignal = computed(() => this.filteredInvoicesSignal().length);

    this.paginatedInvoicesSignal = computed(() => {
      const invoices = this.filteredInvoicesSignal();
      const page = this.pageEventSignal();
      const startIndex = page.pageIndex * page.pageSize;
      return invoices.slice(startIndex, startIndex + page.pageSize);
    });

    this.todasSeleccionadasSignal = computed(() => {
      const facturasFiltradas = this.filteredInvoicesSignal();
      return facturasFiltradas.length > 0 && facturasFiltradas.every(f => f.seleccionada);
    });

    const emisoresUnicosSignal = computed(() => {
      const invoices = this.invoicesSignal();
      const rawValues = invoices.map(f => f.emisorNombre || '');
      const unique = [...new Set(rawValues)];
      const filtered = unique.filter(val => val && val !== 'REVISION_REQUERIDA' && val.toLowerCase() !== 'procesando');
      return filtered.sort();
    });

    const clientesUnicosSignal = computed(() => {
      const invoices = this.invoicesSignal();
      const rawValues = invoices.map(f => f.receptorNombre || '');
      const unique = [...new Set(rawValues)];
      const filtered = unique.filter(val => val && val !== 'REVISION_REQUERIDA' && val.toLowerCase() !== 'procesando');
      return filtered.sort();
    });

    const rfcsUnicosSignal = computed(() => {
      const invoices = this.invoicesSignal();
      const rawValues = invoices.map(f => f.receptorRFC || '');
      const unique = [...new Set(rawValues)];
      const filtered = unique.filter(val => val && val !== 'REVISION_REQUERIDA' && val.toLowerCase() !== 'procesando');
      return filtered.sort();
    });

    // Convertir signals a observables y combinar con valueChanges del form
    const emisores$ = toObservable(emisoresUnicosSignal);
    const clientes$ = toObservable(clientesUnicosSignal);
    const rfcs$ = toObservable(rfcsUnicosSignal);

    this.filteredEmisores$ = combineLatest([
      this.filtroForm.get('emisor')!.valueChanges.pipe(startWith('')),
      emisores$
    ]).pipe(
      map(([value, options]) => this._filterAutocomplete(value || '', options))
    );

    this.filteredClientes$ = combineLatest([
      this.filtroForm.get('cliente')!.valueChanges.pipe(startWith('')),
      clientes$
    ]).pipe(
      map(([value, options]) => this._filterAutocomplete(value || '', options))
    );

    this.filteredRfcs$ = combineLatest([
      this.filtroForm.get('rfc')!.valueChanges.pipe(startWith('')),
      rfcs$
    ]).pipe(
      map(([value, options]) => this._filterAutocomplete(value || '', options))
    );

    // --- EFFECT PARA POLLING ---
    effect(() => {
      const state = this.analysisStatusSignal();

      if (state.is_processing) {
        this.startInvoicePolling();
      } else {
        this.stopInvoicePolling();

        // Si acabamos de terminar (procesando -> no procesando), hacer un último refresh
        if (this.previousIsProcessing) {
          // Análisis finalizado, haciendo refresh final
          this.loadDataForCurrentTab().pipe(
            takeUntil(this.destroy$)
          ).subscribe({
            error: (err) => console.error('Error en refresh post-análisis:', err)
          });
        }
      }

      this.previousIsProcessing = state.is_processing;
    });
  }

  private previousIsProcessing: boolean = false;
  // --- Nueva señal para controlar la pestaña activa (0: Facturas, 1: Pre-facturas, 2: Remisiones)
  activeTabSignal: WritableSignal<number> = signal(0); // 0: Facturas, 1: Pre-facturas, 2: Remisiones

  currentTitle = computed(() => {
    switch (this.activeTabSignal()) {
      case 0: return 'Monitor de Facturas Externas';
      case 1: return 'Monitor de Pre-facturas Externas';
      case 2: return 'Monitor de Remisiones Externas';
      default: return 'Monitor de Facturas Externas';
    }
  });

  // ... constructor ...

  ngOnInit(): void {
    // ngOnInit iniciado

    // Carga inicial basada en el tab 0
    this.currentRequestSubscription = this.loadDataForCurrentTab().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      error: (err) => {
        console.error('Error en carga inicial:', err);
        this.snackBar.open('Error al cargar datos iniciales', 'error');
      }
    });

    this.analysisService.startPolling(); // Inicia el sondeo del estado del análisis

    // Manejar cambios en el formulario general con debounce
    this.filtroForm.valueChanges
      .pipe(
        tap(() => this.isFilteringSignal.set(true)), // Feedback inmediato
        debounceTime(400),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        takeUntil(this.destroy$),
      )
      .subscribe((valores) => {
        // Desacoplar proceso para renderizar spinner con tiempo adecuado
        setTimeout(() => {
          this.filtrosSignal.set(valores);
          if (this.paginator) this.paginator.firstPage();
          this.isFilteringSignal.set(false); // Apagar spinner
        }, 100); // Tiempo mínimo para que el spinner sea visible pero no moleste
      }, (error) => {
        console.error('Error en suscripción de filtros:', error);
        this.isFilteringSignal.set(false); // Asegurar que el spinner se desactive en caso de error
      });
  }

  // Método para manejar el cambio de pestaña
  onTabChange(index: number): void {
    this.activeTabSignal.set(index);
    this.limpiarFiltros(); // Limpiar filtros al cambiar de contexto

    // Cancelar petición anterior si existe
    if (this.currentRequestSubscription) {
      this.currentRequestSubscription.unsubscribe();
    }

    this.currentRequestSubscription = this.loadDataForCurrentTab().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: () => {
        // Resetear paginación al cambiar de tab
        if (this.paginator) {
          this.paginator.firstPage();
        }
      },
      error: (err) => {
        console.error('Error al cargar datos del tab:', err);
        this.snackBar.open('Error al cargar datos', 'error');
      }
    });
  }

  private loadDataForCurrentTab(): Observable<ExternalInvoice[]> {
    const tabIndex = this.activeTabSignal();
    let observable: Observable<ExternalInvoice[]>;
    switch (tabIndex) {
      case 0: // Facturas
        observable = this.externalInvoiceService.loadExternalInvoices();
        break;
      case 1: // Pre-facturas
        observable = this.externalInvoiceService.loadExternalPreInvoices();
        break;
      case 2: // Remisiones
        observable = this.externalInvoiceService.loadExternalReferrals();
        break;
      default:
        observable = this.externalInvoiceService.loadExternalInvoices();
    }
    observable.subscribe();
    return observable;
  }

  private startInvoicePolling(): void {
    if (this.invoicePollingSubscription && !this.invoicePollingSubscription.closed) {
      return; // Ya está activo
    }
    this.invoicePollingSubscription = timer(0, 10000).pipe( // Refresca cada 10 segundos
      switchMap(() => {
        const tabIndex = this.activeTabSignal();
        if (tabIndex === 0) return this.externalInvoiceService.loadExternalInvoices(true, true);
        if (tabIndex === 1) return this.externalInvoiceService.loadExternalPreInvoices(true);
        return this.externalInvoiceService.loadExternalReferrals(true);
      })
    ).subscribe();
  }

  private stopInvoicePolling(): void {
    if (this.invoicePollingSubscription) {
      this.invoicePollingSubscription.unsubscribe();
      this.invoicePollingSubscription = undefined;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.analysisService.stopPolling();
    this.stopInvoicePolling();
  }

  onFacturaSelectionChange(invoiceId: string, checked: boolean): void {
    this.externalInvoiceService.seleccionarFactura(invoiceId, checked);
  }

  onSelectAllFiltradasChange(event: any): void {
    this.externalInvoiceService.seleccionarTodasLasFiltradas(event.checked, this.filteredInvoicesSignal().map(f => f.id));
  }

  aplicarCambioDeEstatus(): void {
    const ids = this.externalInvoiceService.getFacturasSeleccionadasIds();
    if (!this.estatusSeleccionadoParaCambio || ids.length === 0) {
      this.snackBar.open('Seleccione un estatus y al menos una factura.', 'warning');
      return;
    }

    // Determinar el tipo de documento según el tab activo
    let documentType: 'INVOICE' | 'REFERRAL' | 'PRE_INVOICE';
    switch (this.activeTabSignal()) {
      case 0:
        documentType = 'INVOICE';
        break;
      case 1:
        documentType = 'PRE_INVOICE';
        break;
      case 2:
        documentType = 'REFERRAL';
        break;
      default:
        documentType = 'INVOICE';
    }

    // Se agregan los datos requeridos por el constructor de ObservationsDialogComponent
    const dialogRef = this.dialog.open(ObservationsDialogComponent, {
      width: '400px',
      data: {
        facturas: ids,
        status: this.estatusSeleccionadoParaCambio
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.observation) {
        this.isUpdatingStatusSignal.set(true);
        this.externalInvoiceService.changeStatus(
          ids,
          this.estatusSeleccionadoParaCambio,
          result.observation,
          documentType
        )
          .pipe(finalize(() => this.isUpdatingStatusSignal.set(false)))
          .subscribe({
            next: () => {
              this.snackBar.open('Estatus actualizado correctamente.', 'success');
              this.estatusSeleccionadoParaCambio = '';
              this.externalInvoiceService.clearSelection();
            },
            error: () => this.snackBar.open('Error al actualizar el estatus.', 'error')
          });
      }
    });
  }

  getSelectedInvoiceCount(): number {
    return this.externalInvoiceService.getFacturasSeleccionadasIds().length;
  }

  forzarRefrescoCompleto(): void {
    this.snackBar.open('Actualizando datos desde el servidor...', 'info');
    this.limpiarFiltros();

    if (this.currentRequestSubscription) {
      this.currentRequestSubscription.unsubscribe();
    }

    this.currentRequestSubscription = this.loadDataForCurrentTab().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: () => {
        this.snackBar.open('Datos actualizados correctamente', 'success');
        if (this.paginator) {
          this.paginator.firstPage();
        }
      },
      error: (err) => {
        console.error('Error al refrescar datos:', err);
        this.snackBar.open('Error al actualizar datos', 'error');
      }
    });
  }

  limpiarFiltros(): void {
    // CRÍTICO: Desactivar spinner ANTES de resetear el formulario
    // para evitar que valueChanges lo reactive
    this.isFilteringSignal.set(false);

    // Resetear formulario SIN emitir eventos (evita disparar valueChanges)
    this.filtroForm.reset(
      { textoGeneral: '', emisor: '', cliente: '', rfc: '', estatus: '' },
      { emitEvent: false } // ⭐ Evita que valueChanges se dispare
    );

    // Limpiar signal de filtros directamente
    this.filtrosSignal.set({});

    // Resetear paginación
    this.pageEventSignal.set({ pageIndex: 0, pageSize: 20, length: 0 });
  }

  openUploadStatusDialog(): void {
    const dialogRef = this.dialog.open(UploadStatusDialogComponent, {
      width: '900px',
      disableClose: true,
      data: {
        allInvoices: this.invoicesSignal(),
        isAvedi: false
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.snackBar.open('La carga masiva ha finalizado, actualizando vista...', 'info');
      }
    });
  }

  /**
   * Exporta a Excel los documentos actualmente filtrados del tab activo.
   */
  async exportarExcel(): Promise<void> {
    const invoices = this.filteredInvoicesSignal();
    if (!invoices || invoices.length === 0) {
      this.snackBar.open('No hay documentos para exportar.', 'warning');
      return;
    }

    const isReferral = this.activeTabSignal() === 2;
    const data = invoices.map(inv => ({
      documentFolio: inv.documentFolio || '',
      folio: isReferral ? (inv.folioRemision || '') : (inv.folio || ''),
      uuid: inv.uuid || '',
      emisorNombre: inv.emisorNombre || '',
      emisorRFC: inv.emisorRFC || '',
      receptorNombre: inv.receptorNombre || '',
      receptorRFC: inv.receptorRFC || '',
      proveedorNombre: inv.proveedorNombre || '',
      proveedorRFC: inv.proveedorRFC || '',
      estatus: this.getStatusName(inv.estatus),
      total: inv.total,
      fecha: isReferral ? inv.fechaRemision : inv.fechaEmision,
    }));

    const tabName = this.currentTitle().replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ ]/g, '').replace(/ /g, '-');
    const today = new Date().toISOString().slice(0, 10);

    await this.simpleExcel.simpleXLSX({
      fileName: `Monitor-Externo-${tabName}-${today}`,
      sheets: [{
        sheetName: 'Documentos',
        header: [this.currentTitle() + ' - filtrados', `Total: ${data.length}`, 'Epione'],
        lineBreak: 1,
        columns: [
          { columnKey: 'documentFolio', columnName: 'Folio Documento', width: 22 },
          { columnKey: 'folio', columnName: isReferral ? 'Folio Remisión' : 'Folio', width: 16 },
          { columnKey: 'uuid', columnName: 'CFDI (UUID)', width: 40 },
          { columnKey: 'emisorNombre', columnName: 'Emisor', width: 32 },
          { columnKey: 'emisorRFC', columnName: 'RFC Emisor', width: 14 },
          { columnKey: 'receptorNombre', columnName: 'Receptor', width: 32 },
          { columnKey: 'receptorRFC', columnName: 'RFC Receptor', width: 14 },
          { columnKey: 'proveedorNombre', columnName: 'Proveedor', width: 30 },
          { columnKey: 'proveedorRFC', columnName: 'RFC Proveedor', width: 14 },
          { columnKey: 'estatus', columnName: 'Estatus', width: 22 },
          { columnKey: 'fecha', columnName: isReferral ? 'Fecha Remisión' : 'Fecha Emisión', type: 'date', width: 16 },
          { columnKey: 'total', columnName: 'Total', type: 'currency', width: 14 },
        ],
        data,
      }],
    });
    this.snackBar.open(`Excel generado con ${data.length} documento(s).`, 'success');
  }

  openUploadArmstrongDialog(): void {
    const dialogRef = this.dialog.open(UploadArmstrongDialogComponent, {
      width: '90vw',
      maxWidth: '1400px',
      maxHeight: '90vh',
      disableClose: true,
      data: {}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.snackBar.open('Carga Armstrong completada, actualizando vista...', 'info');
        this.forzarRefrescoCompleto();
      }
    });
  }

  /**
   * Abre el diálogo de edición manual de campos de un documento externo.
   */
  openEditInvoiceDialog(invoice: ExternalInvoice, event: Event): void {
    event.stopPropagation();

    let documentType: 'INVOICE' | 'REFERRAL' | 'PRE_INVOICE';
    switch (this.activeTabSignal()) {
      case 1: documentType = 'PRE_INVOICE'; break;
      case 2: documentType = 'REFERRAL'; break;
      default: documentType = 'INVOICE';
    }

    const dialogRef = this.dialog.open(EditExternalInvoiceDialogComponent, {
      width: '680px',
      data: { invoice, documentType }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.id && result.changes) {
        this.externalInvoiceService.updateExternalInvoices([result], documentType).subscribe({
          next: () => this.snackBar.open('Datos actualizados correctamente.', 'success'),
          error: () => this.snackBar.open('Error al actualizar los datos.', 'error')
        });
      }
    });
  }

  /**
   * Determina si el emisor/proveedor de una factura quedó sin RFC resoluble
   * (vacío o valores centinela de IA). Se usa para el badge "Sin RFC".
   */
  hasMissingRfc(invoice: ExternalInvoice): boolean {
    const rfc = this.activeTabSignal() === 2 ? invoice.proveedorRFC : invoice.emisorRFC;
    if (rfc && rfc.trim()) {
      const upper = rfc.trim().toUpperCase();
      return upper === 'NO ENCONTRADO' || upper === 'NO_ENCONTRADO' || upper === 'REVISION_REQUERIDA';
    }
    return true;
  }

  goBack(): void {
    this.router.navigate(['/home']);
  }

  onCopyToClipboard(text: string): void {
    this.snackBar.open(`'${text}' copiado al portapapeles.`, 'info');
  }

  /**
   * @description Determines the color for the analysis status icon
   * @returns The Material color name for the icon
   */
  getAnalysisStatusColor(): string {
    const state = this.analysisStatusSignal();
    if (!state) return 'accent';

    if (state.is_processing) {
      return 'warn'; // Procesando
    }

    if (state.history.length > 0 && state.history[0].status === 'ERROR') {
      return 'warn'; // Error
    }

    return 'primary'; // Inactivo o completado
  }

  private _filterAutocomplete(value: string, options: string[]): string[] {
    const filterValue = (value || '').toLowerCase();
    return options.filter(option => (option || '').toLowerCase().includes(filterValue));
  }


  getFieldDisplayState(value: any, fieldKey: string): 'VALIDO' | 'PROCESANDO' | 'REVISION_REQUERIDA' | 'NO_ENCONTRADO' | 'NO ENCONTRADO' {
    if (value === 'procesando') {
      return 'PROCESANDO';
    }

    const valueStr = String(value || '').toUpperCase().trim();

    // NUEVO: Manejo explícito y robusto de "NO ENCONTRADO"
    if (valueStr === 'NO ENCONTRADO' || valueStr === 'NO_ENCONTRADO') {
      return 'NO_ENCONTRADO';
    }

    if (value === 'REVISION_REQUERIDA' || value === null || value === undefined || value === '') {
      return 'NO_ENCONTRADO';
    }

    const valueAsString = String(value);
    const isValid = this.validationService.validateField(fieldKey, valueAsString);

    return isValid ? 'VALIDO' : 'REVISION_REQUERIDA';
  }

  getStatusName(statusKey: string): string {
    return this.statusTranslator.translate(statusKey, 'cobranza');
  }

  /**
   * Color del catálogo para el chip de estatus (undefined => usa el CSS por defecto).
   */
  getStatusColor(estatus: string): string | undefined {
    return this.statusTranslator.getColor(estatus);
  }

  /**
   * Muestra el valor de un campo o un placeholder si requiere revisión
   * @param value Valor del campo
   * @param fieldName Nombre del campo (para logging)
   * @returns Texto a mostrar en la UI
   */
  displayFieldValue(value: any, fieldName: string): string {
    if (value === 'REVISION_REQUERIDA' || value === null || value === '' || value === undefined) {
      return '⚠️ REVISIÓN REQUERIDA';
    }
    return value;
  }

  /**
   * Verifica si un campo requiere revisión manual
   * @param value Valor del campo
   * @returns true si el campo requiere revisión
   */
  isFieldRequiresRevision(value: any): boolean {
    return value === 'REVISION_REQUERIDA' || value === null || value === '' || value === undefined;
  }

  getSerieFolioState(serie: any, folio: any): 'VALIDO' | 'PROCESANDO' | 'REVISION_REQUERIDA' | 'NO_ENCONTRADO' | 'NO ENCONTRADO' {
    // Verificar si estamos procesando
    if (serie === 'procesando' || folio === 'procesando') return 'PROCESANDO';

    // Verificar si hay revision requerida explícita
    if (serie === 'REVISION_REQUERIDA' || folio === 'REVISION_REQUERIDA') return 'REVISION_REQUERIDA';

    const isSerieInvalid = !serie || serie === 'NO ENCONTRADO' || serie === 'NO_ENCONTRADO';
    const isFolioInvalid = !folio || folio === 'NO ENCONTRADO' || folio === 'NO_ENCONTRADO';

    // Si ambos faltan -> NO_ENCONTRADO
    if (isSerieInvalid && isFolioInvalid) {
      return 'NO_ENCONTRADO';
    }

    // Si al menos uno es válido -> VALIDO (para mostrar parcial)
    return 'VALIDO';
  }

  formatSerieFolio(serie: any, folio: any): string {
    const cleanSerie = (serie && serie !== 'NO ENCONTRADO' && serie !== 'REVISION_REQUERIDA' && serie !== 'procesando') ? String(serie) : '';
    const cleanFolio = (folio && folio !== 'NO ENCONTRADO' && folio !== 'REVISION_REQUERIDA' && folio !== 'procesando') ? String(folio) : '';

    if (cleanSerie && cleanFolio) {
      // Evitar duplicados si serie == folio
      if (cleanSerie === cleanFolio) return cleanFolio;
      return `${cleanSerie}-${cleanFolio}`;
    }

    // Si solo hay uno, agregamos prefijo para claridad
    if (cleanSerie) return `Serie: ${cleanSerie}`;
    if (cleanFolio) return `Folio: ${cleanFolio}`;

    return '';
  }

  /**
   * Agrupa el historial de estatus separando los cambios de IA de los manuales
   * @param statusHistory Array de historial de estatus
   * @returns Objeto con iaChanges (agrupados) y manualChanges (individuales)
   */
  groupStatusHistory(statusHistory: any[]): { iaChanges: any[], manualChanges: any[] } {
    if (!statusHistory || statusHistory.length === 0) {
      return { iaChanges: [], manualChanges: [] };
    }

    const iaStatuses = ['EN_PROCESO', 'PROCESANDO', 'PROCESADO_IA', 'ERROR_IA'];
    const iaChanges: any[] = [];
    const manualChanges: any[] = [];

    statusHistory.forEach(history => {
      const status = history.estatus || '';
      const observation = (history.observacion || '').toLowerCase();

      // Determinar si es un cambio de IA
      const isIAChange = iaStatuses.includes(status) ||
        observation.includes('ia') ||
        observation.includes('procesando') ||
        observation.includes('extracción') ||
        observation.includes('análisis');

      if (isIAChange) {
        iaChanges.push(history);
      } else {
        manualChanges.push(history);
      }
    });

    return { iaChanges, manualChanges };
  }

}